import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  Sparkles, Waves, Zap, Moon, Target, Heart, Play, Pause,
  SkipBack, Clock, Star, Volume2, Loader2, ChevronDown, Lock, Crown,
  Download, Check
} from "lucide-react";
import { toast } from "sonner";
import { devWarn } from "../utils/devLogger";
import { useNavigate } from "react-router-dom";

const THEME_ICONS = {
  stress_relief: Waves,
  energy_boost: Zap,
  sleep_prep: Moon,
  focus_sharpening: Target,
  recovery: Heart,
  auto: Sparkles,
};

const THEME_COLORS = {
  stress_relief: "from-sky-400 to-cyan-400",
  energy_boost: "from-amber-400 to-orange-400",
  sleep_prep: "from-indigo-400 to-violet-400",
  focus_sharpening: "from-emerald-400 to-teal-400",
  recovery: "from-pink-400 to-rose-400",
  auto: "from-sky-400 to-indigo-400",
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// Audio Player Component
const MeditationPlayer = ({ meditation, onBack, onFavorite }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFav, setIsFav] = useState(meditation.is_favorite);
  const listenedRef = useRef(0);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      const now = audio.currentTime;
      // Track active listening (only count forward progress, not seeks)
      if (isPlaying && now > lastTimeRef.current && now - lastTimeRef.current < 2) {
        listenedRef.current += (now - lastTimeRef.current);
      }
      lastTimeRef.current = now;
      setCurrentTime(now);
    };
    const onDur = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnd = () => {
      setIsPlaying(false);
      // Log total listened time
      const secs = Math.round(listenedRef.current);
      if (secs > 0) {
        axios.post(`${API}/meditation/ai/${meditation.id}/log-time`, { seconds: secs }).catch(() => {});
      }
      axios.post(`${API}/meditation/ai/${meditation.id}/play`).catch(() => {});
    };
    const onError = (e) => {
      console.error("Audio load error:", e);  // eslint-disable-line no-console
      toast.error(t('mindfulness.aiMeditation.audioFailed'));
    };
    const onCanPlay = () => {
      if (audio.duration && isFinite(audio.duration) && duration === 0) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onDur);
    audio.addEventListener("durationchange", onDur);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);

    // Log time when user navigates away
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onDur);
      audio.removeEventListener("durationchange", onDur);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
      // Log accumulated time on unmount
      const secs = Math.round(listenedRef.current);
      if (secs > 5) {
        axios.post(`${API}/meditation/ai/${meditation.id}/log-time`, { seconds: secs }).catch(() => {});
      }
    };
  }, [meditation.id, duration, isPlaying]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
    setIsPlaying(!isPlaying);
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const handleFav = async () => {
    try {
      const res = await axios.post(`${API}/meditation/ai/${meditation.id}/favorite`);
      setIsFav(res.data.is_favorite);
      onFavorite?.(meditation.id, res.data.is_favorite);
    } catch { /* silent */ }
  };

  const handleDownload = async () => {
    try {
      const baseUrl = `${API.replace("/api", "")}`;
      const audioUrl = `${baseUrl}${meditation.audio_url}`;
      const res = await fetch(audioUrl);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const mp3Blob = new Blob([blob], { type: "audio/mpeg" });
      const url = URL.createObjectURL(mp3Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${meditation.title.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_")}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(t('mindfulness.aiMeditation.downloaded'));
    } catch {
      toast.error(t('mindfulness.aiMeditation.downloadFailed'));
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const themeGrad = THEME_COLORS[meditation.theme] || THEME_COLORS.auto;
  const ThemeIcon = THEME_ICONS[meditation.theme] || Sparkles;

  return (
    <div className="text-center" data-testid="meditation-player">
      <audio ref={audioRef} src={`${API.replace("/api", "")}${meditation.audio_url}`} preload="metadata" />

      {/* Theme visual */}
      <div className="relative w-48 h-48 mx-auto mb-8">
        <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${themeGrad} opacity-20 ${isPlaying ? "animate-pulse" : ""}`}
          style={{ animationDuration: "4s" }} />
        <div className={`absolute inset-4 rounded-full bg-gradient-to-br ${themeGrad} opacity-10 ${isPlaying ? "animate-pulse" : ""}`}
          style={{ animationDuration: "3s", animationDelay: "0.5s" }} />
        <div className="absolute inset-8 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur flex items-center justify-center">
          <ThemeIcon className={`w-12 h-12 ${isPlaying ? "text-sky-500 animate-pulse" : "text-sky-400"}`} strokeWidth={1.5} />
        </div>
      </div>

      <h2 className="text-2xl font-light text-sky-900 dark:text-sky-100 mb-1" style={{ fontFamily: "Outfit" }}>
        {meditation.title}
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {meditation.theme_label} · {meditation.duration_minutes} min · {meditation.voice}
      </p>

      {/* Progress bar */}
      <div className="max-w-md mx-auto mb-2 cursor-pointer" onClick={seek} data-testid="progress-bar">
        <div className="h-1.5 bg-sky-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${themeGrad} transition-all`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <div className="flex justify-between max-w-md mx-auto text-xs text-slate-400 mb-6">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <div className="flex flex-col items-center gap-0.5">
          <Button variant="ghost" size="icon" onClick={handleFav} data-testid="favorite-btn"
            className={isFav ? "text-amber-500" : "text-slate-400"}>
            <Star className={`w-5 h-5 ${isFav ? "fill-amber-400" : ""}`} />
          </Button>
          <span className="text-[9px] font-medium tracking-wider text-slate-400 uppercase">
            {isFav ? t('mindfulness.aiMeditation.saved') : t('mindfulness.aiMeditation.save')}
          </span>
        </div>
        <Button
          onClick={togglePlay}
          className={`w-16 h-16 rounded-full bg-gradient-to-r ${themeGrad} text-white hover:opacity-90 shadow-lg`}
          data-testid="play-pause-btn"
        >
          {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
        </Button>
        <Button variant="ghost" size="icon" onClick={onBack} className="text-slate-400" data-testid="back-to-list-btn">
          <SkipBack className="w-5 h-5" />
        </Button>
        <div className="flex flex-col items-center gap-0.5">
          <Button variant="ghost" size="icon" onClick={handleDownload} data-testid="download-meditation-btn"
            className="text-slate-400 hover:text-sky-500">
            <Download className="w-5 h-5" />
          </Button>
          <span className="text-[9px] font-medium tracking-wider text-slate-400 uppercase">{t('mindfulness.aiMeditation.save')}</span>
        </div>
      </div>
    </div>
  );
};

// Generation Loader — immersive full-screen experience matching training plan style
const MEDITATION_STAGES = [
  { key: "analyzing", icon: Heart, label: "Reading your emotional profile", color: "#f472b6" },
  { key: "crafting", icon: Sparkles, label: "Crafting meditation script", color: "#818cf8" },
  { key: "narrating", icon: Volume2, label: "Generating soothing narration", color: "#22d3ee" },
  { key: "finalizing", icon: Star, label: "Polishing the experience", color: "#34d399" },
];

const GenerationLoader = ({ progress, t }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const pts = Array.from({ length: 16 }, (_, i) => ({
      id: i, x: Math.random() * 100, y: Math.random() * 100,
      size: 2 + Math.random() * 4, delay: Math.random() * 5, duration: 3 + Math.random() * 4,
    }));
    setParticles(pts);
  }, []);

  useEffect(() => {
    if (progress < 10) setActiveStage(0);
    else if (progress < 25) setActiveStage(1);
    else if (progress < 90) setActiveStage(2);
    else setActiveStage(3);
  }, [progress]);

  const stageColor = MEDITATION_STAGES[activeStage]?.color || "#818cf8";
  const isDone = progress >= 100;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden" data-testid="generation-loader">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950" />

      {particles.map(p => (
        <div key={p.id} className="absolute rounded-full opacity-20"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: `${p.size}px`, height: `${p.size}px`,
            background: stageColor, animation: `med-float ${p.duration}s ease-in-out infinite`, animationDelay: `${p.delay}s`,
          }} />
      ))}

      <div className="absolute w-96 h-96 rounded-full blur-3xl opacity-20 transition-colors duration-1000" style={{ background: stageColor }} />

      <div className="relative z-10 max-w-lg w-full mx-4 text-center">
        <div className="mb-10 flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl flex items-center justify-center transition-all duration-700"
              style={{ background: `linear-gradient(135deg, ${stageColor}33, ${stageColor}11)`, boxShadow: `0 0 60px ${stageColor}33` }}>
              {isDone ? (
                <Sparkles className="w-12 h-12 text-emerald-400" strokeWidth={1.5} style={{ animation: "med-scale-in 0.5s ease-out" }} />
              ) : (
                (() => { const Icon = MEDITATION_STAGES[activeStage]?.icon || Sparkles;
                  return <Icon className="w-12 h-12 text-white" strokeWidth={1.5} style={{ animation: "med-pulse 2s ease-in-out infinite" }} />;
                })()
              )}
            </div>
            {!isDone && <div className="absolute inset-[-12px] rounded-2xl border border-white/10" style={{ animation: "med-spin 8s linear infinite" }} />}
          </div>
        </div>

        <div className="mb-3">
          {isDone ? (
            <h2 className="text-2xl font-light text-emerald-400" style={{ fontFamily: "Outfit", animation: "med-fade-in 0.5s ease-out" }}>
              {t('mindfulness.aiMeditation.stageAlmost')}
            </h2>
          ) : (
            <h2 key={activeStage} className="text-xl sm:text-2xl font-light text-white/90" style={{ fontFamily: "Outfit", animation: "med-text-in 0.5s ease-out" }}>
              {MEDITATION_STAGES[activeStage]?.label}
            </h2>
          )}
        </div>

        <p className="text-sm text-white/40 mb-8" style={{ fontFamily: "Outfit" }}>
          {t('mindfulness.aiMeditation.pleaseBePatient') || 'Please be patient.'}
        </p>

        <div className="mb-8 px-4">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.max(progress, 3)}%`,
                background: `linear-gradient(90deg, ${MEDITATION_STAGES[Math.max(0, activeStage - 1)]?.color || "#818cf8"}, ${stageColor})`,
                boxShadow: `0 0 12px ${stageColor}66`,
              }} />
          </div>
          <p className="text-xs text-white/30 mt-2 font-mono">{progress}%</p>
        </div>

        <div className="space-y-2 px-4 text-left">
          {MEDITATION_STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const isCompleted = idx < activeStage || isDone;
            const isCurrent = idx === activeStage && !isDone;
            const isPending = idx > activeStage;
            return (
              <div key={stage.key}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-500 ${isCurrent ? "bg-white/5" : isCompleted ? "bg-white/[0.02]" : ""}`}
                style={{ opacity: isPending ? 0.25 : 1 }}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isCompleted ? "bg-emerald-500/20" : isCurrent ? "bg-white/10" : "bg-white/5"}`}>
                  {isCompleted ? <Check className="w-4 h-4 text-emerald-400" strokeWidth={2} /> : <Icon className={`w-4 h-4 ${isCurrent ? "text-white" : "text-white/30"}`} strokeWidth={1.5} />}
                </div>
                <span className={`text-sm transition-all duration-500 ${isCompleted ? "text-emerald-400/80" : isCurrent ? "text-white/90" : "text-white/25"}`}>{stage.label}</span>
                {isCurrent && (
                  <div className="ml-auto flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes med-float { 0%, 100% { transform: translateY(0) translateX(0); opacity: 0.15; } 25% { transform: translateY(-30px) translateX(10px); opacity: 0.3; } 50% { transform: translateY(-10px) translateX(-15px); opacity: 0.1; } 75% { transform: translateY(-40px) translateX(5px); opacity: 0.25; } }
        @keyframes med-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes med-pulse { 0%, 100% { opacity: 0.8; transform: scale(1); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes med-text-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes med-scale-in { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
        @keyframes med-fade-in { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>,
    document.body
  );
};

// Main AI Meditation Component
const AIMeditation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [durations, setDurations] = useState([5]);
  const [voices, setVoices] = useState([]);
  const [library, setLibrary] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState(["auto"]);
  const [selectedDuration, setSelectedDuration] = useState(5);
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [activeMeditation, setActiveMeditation] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [quota, setQuota] = useState(null);
  const [themeConflicts, setThemeConflicts] = useState([]);
  const [meditationStats, setMeditationStats] = useState(null);

  const _downloadMeditation = async (med) => {
    try {
      const baseUrl = API.replace("/api", "");
      const audioUrl = `${baseUrl}${med.audio_url}`;
      const res = await fetch(audioUrl);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const mp3Blob = new Blob([blob], { type: "audio/mpeg" });
      const url = URL.createObjectURL(mp3Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(med.title || "meditation").replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_")}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Download started");
    } catch {
      toast.error("Download failed");
    }
  };

  const fetchQuota = async () => {
    try {
      const res = await axios.get(`${API}/meditation/ai/quota`);
      setQuota(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [themesRes, libraryRes, conflictsRes, statsRes] = await Promise.all([
          axios.get(`${API}/meditation/ai/themes`),
          axios.get(`${API}/meditation/ai/library`),
          axios.get(`${API}/meditation/ai/theme-conflicts`).catch(() => ({ data: { conflicts: [] } })),
          axios.get(`${API}/meditation/ai/stats`).catch(() => ({ data: { total_minutes: 0, total_sessions: 0 } })),
        ]);
        setThemes(themesRes.data.themes || []);
        setDurations(themesRes.data.durations || [5]);
        setVoices(themesRes.data.voices || []);
        setLibrary(libraryRes.data.meditations || []);
        setThemeConflicts(conflictsRes.data.conflicts || []);
        setMeditationStats(statsRes.data);
      } catch { /* silent */ }
    };
    fetch();
    fetchQuota();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenProgress(0);
    try {
      const payload = {
        duration_minutes: selectedDuration,
        voice: selectedVoice,
        language: i18n.language || "en",
      };
      // Multi-theme support
      if (selectedThemes.length === 1) {
        payload.theme = selectedThemes[0];
      } else {
        payload.theme = selectedThemes[0];
        payload.themes = selectedThemes.filter(t => t !== "auto");
      }

      const startRes = await axios.post(`${API}/meditation/ai/generate`, payload);

      // Cache hit — meditation already exists, skip polling
      if (startRes.data.cached && startRes.data.meditation) {
        setActiveMeditation(startRes.data.meditation);
        setLibrary(prev => [startRes.data.meditation, ...prev]);
        toast.success(t('mindfulness.aiMeditation.meditationReadyCached'));
        fetchQuota();
        setGenerating(false);
        return;
      }

      const taskId = startRes.data.task_id;
      if (!taskId) {
        // Fallback: old-style synchronous response (shouldn't happen)
        if (startRes.data.id) {
          setActiveMeditation(startRes.data);
          setLibrary(prev => [startRes.data, ...prev]);
          toast.success(t('mindfulness.aiMeditation.meditationReady'));
          fetchQuota();
          setGenerating(false);
          return;
        }
        throw new Error("No task_id returned");
      }

      // Step 2: Poll for status every 4 seconds
      const poll = async () => {
        const maxAttempts = 180; // 12 minutes max (180 * 4s)
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise(r => setTimeout(r, 4000));
          try {
            const statusRes = await axios.get(`${API}/meditation/ai/task/${taskId}`);
            const { status, progress, meditation, error } = statusRes.data;

            setGenProgress(progress || 0);

            if (status === "complete" && meditation) {
              setActiveMeditation(meditation);
              setLibrary(prev => [meditation, ...prev]);
              toast.success(t('mindfulness.aiMeditation.meditationReady'));
              fetchQuota();
              return;
            }

            if (status === "failed") {
              toast.error(error || "Generation failed. Please try again.");
              return;
            }
          } catch (pollErr) {
            // Network blip — keep polling
            devWarn("Poll error:", pollErr.message);
          }
        }
        toast.error("Generation timed out. Please try again.");
      };

      await poll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start meditation generation");
    } finally {
      setGenerating(false);
      setGenProgress(0);
    }
  };

  const handleFavorite = (id, isFav) => {
    setLibrary(prev => prev.map(m => m.id === id ? { ...m, is_favorite: isFav } : m));
  };

  if (generating) return <GenerationLoader progress={genProgress} t={t} />;

  if (activeMeditation) {
    return (
      <MeditationPlayer
        meditation={activeMeditation}
        onBack={() => setActiveMeditation(null)}
        onFavorite={handleFavorite}
      />
    );
  }

  const favorites = library.filter(m => m.is_favorite);

  const isThemeConflicting = (themeId) => {
    if (selectedThemes.length === 0) return false;
    return themeConflicts.some(pair =>
      (pair.includes(themeId) && selectedThemes.some(s => pair.includes(s) && s !== themeId))
    );
  };

  const toggleTheme = (themeId) => {
    if (selectedThemes.includes(themeId)) {
      // Deselect
      const next = selectedThemes.filter(t => t !== themeId);
      setSelectedThemes(next.length === 0 ? ["auto"] : next);
    } else {
      // If selecting "auto", clear others
      if (themeId === "auto") {
        setSelectedThemes(["auto"]);
        return;
      }
      // Remove "auto" when selecting specific themes
      const withoutAuto = selectedThemes.filter(t => t !== "auto");
      if (withoutAuto.length >= 2) {
        // Replace oldest
        setSelectedThemes([withoutAuto[1], themeId]);
      } else {
        setSelectedThemes([...withoutAuto, themeId]);
      }
    }
  };

  return (
    <div data-testid="ai-meditation-section">
      {/* Meditation Stats Banner */}
      {meditationStats && meditationStats.total_minutes > 0 && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/30" data-testid="meditation-stats">
          <Clock className="w-4 h-4 text-sky-500" strokeWidth={1.5} />
          <span className="text-sm text-sky-700 dark:text-sky-300">
            <strong>{meditationStats.total_minutes}</strong> min meditated · <strong>{meditationStats.total_sessions}</strong> sessions
          </span>
        </div>
      )}

      {/* Theme Selector (multi-select up to 2) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium text-sky-800 dark:text-sky-200" style={{ fontFamily: "Outfit" }}>
            {t('mindfulness.aiMeditation.chooseTheme')}
          </h3>
          <span className="text-xs text-slate-400">Select up to 2</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {themes.map(th => {
            const Icon = THEME_ICONS[th.id] || Sparkles;
            const isSelected = selectedThemes.includes(th.id);
            const isConflict = !isSelected && isThemeConflicting(th.id);
            const label = t(`mindfulness.aiMeditation.themes.${th.id}.label`, th.label);
            const desc = t(`mindfulness.aiMeditation.themes.${th.id}.description`, th.description);
            return (
              <button
                key={th.id}
                onClick={() => !isConflict && toggleTheme(th.id)}
                disabled={isConflict}
                className={`p-3 rounded-xl text-left transition-all ${
                  isSelected
                    ? "bg-sky-100 dark:bg-sky-900/40 border-2 border-sky-400 shadow-sm"
                    : isConflict
                    ? "bg-slate-50 dark:bg-slate-800/30 border-2 border-transparent opacity-40 cursor-not-allowed"
                    : "bg-white/50 dark:bg-slate-800/50 border-2 border-transparent hover:border-sky-200"
                }`}
                data-testid={`theme-${th.id}`}
              >
                <Icon className={`w-5 h-5 mb-1 ${isSelected ? "text-sky-500" : isConflict ? "text-slate-300" : "text-slate-400"}`} strokeWidth={1.5} />
                <p className={`text-sm font-medium ${isSelected ? "text-sky-700 dark:text-sky-300" : "text-slate-600 dark:text-slate-400"}`}>
                  {label}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{desc}</p>
                {isConflict && <p className="text-[10px] text-red-400 mt-0.5">Conflicts</p>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration + Voice */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <h3 className="text-sm font-medium text-sky-800 dark:text-sky-200 mb-2">{t('mindfulness.aiMeditation.duration')}</h3>
          <div className="flex gap-2">
            {durations.map(d => (
              <button
                key={d}
                onClick={() => setSelectedDuration(d)}
                className={`flex-1 py-2 rounded-lg text-sm transition-all ${
                  selectedDuration === d
                    ? "bg-sky-400 text-white shadow"
                    : "bg-white/60 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-sky-50"
                }`}
                data-testid={`duration-${d}`}
              >
                ~{d}m
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-sky-800 dark:text-sky-200 mb-2">{t('mindfulness.aiMeditation.voice')}</h3>
          <div className="flex gap-2 flex-wrap">
            {voices.map(v => (
              <button
                key={v.id}
                onClick={() => setSelectedVoice(v.id)}
                className={`px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedVoice === v.id
                    ? "bg-sky-400 text-white shadow"
                    : "bg-white/60 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-sky-50"
                }`}
                data-testid={`voice-${v.id}`}
              >
                {t(`mindfulness.aiMeditation.voices.${v.id}.label`, v.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Quota Info + Generate Button */}
      {quota && quota.remaining <= 0 && quota.tier === "free" ? (
        <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200" data-testid="meditation-paywall">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">{t('mindfulness.aiMeditation.freeTrialsUsed')}</p>
              <p className="text-xs text-amber-600 mt-0.5">
                {t('mindfulness.aiMeditation.freeTrialsDesc')}
              </p>
              <Button
                onClick={() => navigate("/subscription")}
                className="mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm px-4 py-2"
                data-testid="meditation-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-1.5" /> {t('mindfulness.aiMeditation.upgradePremium')}
              </Button>
            </div>
          </div>
        </div>
      ) : quota && quota.remaining <= 0 && quota.tier === "pro" ? (
        <div className="mb-8 p-3 rounded-xl bg-sky-50 border border-sky-200 text-center" data-testid="meditation-daily-limit">
          <p className="text-sm text-sky-700">{t('mindfulness.aiMeditation.dailyLimitReached', { limit: quota.limit })}</p>
        </div>
      ) : (
        <>
          {quota && quota.tier !== "founder" && (
            <div className="flex items-center justify-between mb-2 px-1" data-testid="meditation-quota-info">
              <span className="text-xs text-slate-500">
                {quota.tier === "pro"
                  ? t('mindfulness.aiMeditation.generationsRemainingToday', { remaining: quota.remaining, limit: quota.limit })
                  : t('mindfulness.aiMeditation.generationsRemaining', { remaining: quota.remaining, limit: quota.limit })}
              </span>
              {quota.tier === "free" && quota.remaining > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-600 font-medium">
                  {t('mindfulness.aiMeditation.freeTrial')}
                </span>
              )}
            </div>
          )}
          <Button
            onClick={handleGenerate}
            className="w-full glow-button py-6 text-base mb-8"
            disabled={quota && quota.remaining <= 0}
            data-testid="generate-meditation-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {t('mindfulness.aiMeditation.generateBtn')}
          </Button>
        </>
      )}

      {/* Favorites */}
      {favorites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-base font-medium text-sky-800 dark:text-sky-200 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" /> {t('mindfulness.aiMeditation.favorites')}
          </h3>
          <div className="space-y-2">
            {favorites.map(med => {
              const Icon = THEME_ICONS[med.theme] || Sparkles;
              return (
                <button
                  key={med.id}
                  onClick={() => setActiveMeditation(med)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/50 dark:bg-slate-800/50 hover:bg-sky-50 dark:hover:bg-slate-700 transition-all text-left"
                  data-testid={`fav-${med.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${THEME_COLORS[med.theme] || THEME_COLORS.auto} flex items-center justify-center opacity-80`}>
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-sky-800 dark:text-sky-200 truncate">{med.title}</p>
                    <p className="text-xs text-slate-400">{med.duration_minutes}m · {med.voice}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      role="button"
                      className="p-1 rounded-md hover:bg-sky-100 dark:hover:bg-slate-600 transition-colors"
                      onClick={(e) => { e.stopPropagation(); _downloadMeditation(med); }}
                      data-testid={`download-fav-${med.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-slate-400 hover:text-sky-500" />
                    </span>
                    <Play className="w-4 h-4 text-sky-400" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Library */}
      {library.length > 0 && (
        <div>
          <button
            onClick={() => setShowLibrary(!showLibrary)}
            className="w-full flex items-center justify-between text-sm font-medium text-sky-800 dark:text-sky-200 mb-3"
            data-testid="toggle-library"
          >
            <span style={{ fontFamily: "Outfit" }}>{t('mindfulness.aiMeditation.yourLibrary')} ({library.length})</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showLibrary ? "rotate-180" : ""}`} />
          </button>
          {showLibrary && (
            <div className="space-y-2">
              {library.map(med => {
                const Icon = THEME_ICONS[med.theme] || Sparkles;
                return (
                  <button
                    key={med.id}
                    onClick={() => setActiveMeditation(med)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/50 dark:bg-slate-800/50 hover:bg-sky-50 dark:hover:bg-slate-700 transition-all text-left"
                    data-testid={`library-${med.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${THEME_COLORS[med.theme] || THEME_COLORS.auto} flex items-center justify-center opacity-80`}>
                      <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-sky-800 dark:text-sky-200 truncate">{med.title}</p>
                      <p className="text-xs text-slate-400">
                        {med.theme_label} · {med.duration_minutes}m · {new Date(med.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                    {med.is_favorite && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                    <span
                      role="button"
                      className="p-1 rounded-md hover:bg-sky-100 dark:hover:bg-slate-600 transition-colors"
                      onClick={(e) => { e.stopPropagation(); _downloadMeditation(med); }}
                      data-testid={`download-lib-${med.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-slate-400 hover:text-sky-500" />
                    </span>
                    <Play className="w-4 h-4 text-sky-400" />
                  </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIMeditation;
